import { communityInboxApi } from "@circle-react/api/communityInboxApi";
import { markPriorityInboxThreadReadInCache } from "@circle-react/lib/queries/priorityInboxThreads";
import type { ChatType as AdminChatFilterType } from "@circle-react/types/CommunityBot/ChatSort";
import type { Chat } from "@circle-react/types/CommunityInbox/Chat";
import type { Message } from "@circle-react/types/CommunityInbox/Message";
import { isEqual, uniqBy } from "lodash";

import { ACTIONS } from "@/react/components/SettingsApp/CommunityInbox/hooks/scrollConstants";

import type { SliceStateCreator } from "../types";
import type {
  ChatType,
  CommunityInboxSliceState,
} from "./communityInboxSliceTypes";
import {
  applyUnreadFlagForNewMessage,
  markAdminChatReadAndGetPausedDelta,
  markMemberChatReadAndGetUnreadDelta,
  patchAgentEnabledInChats,
} from "./createCommunityInboxSlice.helpers";
import {
  markAdminChatAsRead,
  nextAdminChatsAfterMarkAllAsRead,
} from "./markAdminChatAsRead";

export const createCommunityInboxSlice: SliceStateCreator<
  CommunityInboxSliceState
> = (set, get) => ({
  inbox: {
    currentChatId: null,
    setCurrentChatId: (chatId: number) =>
      set(state => {
        state.inbox.currentChatId = chatId;
      }),
    memberChats: [],
    adminChats: [],
    searchChats: [],
    previewChat: null,
    totalUnreadChatsCount: 0,
    totalUnreadPausedAiChatsCount: 0,
    isTotalUnreadPausedAiChatsCountLoaded: false,
    setChats: (chats: Chat[], type: ChatType) => {
      const chatsKeyName = type === "member" ? "memberChats" : "adminChats";
      set(state => {
        state.inbox[chatsKeyName] = chats;
      });
    },
    setSearchChats: (chats: Chat[]) => {
      set(state => {
        state.inbox.searchChats = chats;
      });
    },
    appendChats: (chats: Chat[], type: ChatType) => {
      const chatsKeyName = type === "member" ? "memberChats" : "adminChats";
      set(state => {
        state.inbox[chatsKeyName] = uniqBy(
          [...get().inbox[chatsKeyName], ...chats],
          "id",
        );
      });
    },
    setPreviewChat: (chat: Chat) => {
      set(state => {
        state.inbox.previewChat = chat;
      });
    },
    fetchChat: async (chatId: number, type: ChatType) => {
      const chat = await communityInboxApi.fetchChat({ id: chatId });
      get().inbox.updateChat(chat, type);
    },
    appendChat: (chat: Chat, type: ChatType) => {
      const chatsKeyName = type === "member" ? "memberChats" : "adminChats";
      if (get().inbox[chatsKeyName].some(c => c.id === chat.id)) {
        return;
      }
      set(state => {
        state.inbox[chatsKeyName] = [chat, ...state.inbox[chatsKeyName]];
      });
    },
    updateChat: (chat: Chat, type: ChatType) => {
      const chatsKeyName = type === "member" ? "memberChats" : "adminChats";
      const chats = get().inbox[chatsKeyName];
      const index = chats.findIndex(c => c.id === chat.id);
      // Not loaded yet (e.g. a chat that just received a new message): append it placing it at the beginning of the list.
      if (index === -1) {
        get().inbox.appendChat(chat, type);
        return;
      }
      // Skip no-op updates so redundant broadcasts don't trigger re-renders.
      if (isEqual(chats[index], chat)) {
        return;
      }
      set(state => {
        state.inbox[chatsKeyName][index] = chat;
      });
    },
    updateAgentChats: (
      agentId: number,
      enabled: boolean,
      chats?: { id: number; disabled: boolean }[],
    ) =>
      set(state => {
        const chatDisabledById = chats?.reduce<Record<number, boolean>>(
          (acc, chat) => {
            acc[chat.id] = chat.disabled;
            return acc;
          },
          {},
        );
        patchAgentEnabledInChats(
          state.inbox.memberChats,
          agentId,
          enabled,
          chatDisabledById,
        );
        patchAgentEnabledInChats(
          state.inbox.adminChats,
          agentId,
          enabled,
          chatDisabledById,
        );
      }),
    destroyChat: (chatId: number, type: ChatType) => {
      const chatsKeyName = type === "member" ? "memberChats" : "adminChats";
      set(state => {
        state.inbox[chatsKeyName] = state.inbox[chatsKeyName].filter(
          c => c.id !== chatId,
        );
      });
    },
    markAllAdminChatsAsRead: (activeChatType?: AdminChatFilterType) => {
      set(state => {
        state.inbox.adminChats = nextAdminChatsAfterMarkAllAsRead(
          state.inbox.adminChats,
          activeChatType,
        );
        state.inbox.searchChats =
          state.inbox.searchChats.map(markAdminChatAsRead);
      });
      void get().inbox.fetchPausedAiUnreadChatsCount();
    },
    adminMarkAsRead: (chatId: number) => {
      communityInboxApi
        .adminMarkAsRead({ id: chatId })
        .then(() => {
          markPriorityInboxThreadReadInCache({
            communityInboxChatId: chatId,
          });
          set(state => {
            state.inbox.totalUnreadPausedAiChatsCount +=
              markAdminChatReadAndGetPausedDelta(
                state.inbox.adminChats,
                chatId,
              );
          });
        })
        .catch(err => {
          console.error(err);
        });
    },
    memberMarkAsRead: (chatId: number) => {
      communityInboxApi
        .memberMarkAsRead({ id: chatId })
        .then(() => {
          set(state => {
            state.inbox.totalUnreadChatsCount +=
              markMemberChatReadAndGetUnreadDelta(
                state.inbox.memberChats,
                chatId,
              );
          });
        })
        .catch(err => {
          console.error(err);
        });
    },
    messages: [],
    setMessages: (messages: Message[]) =>
      set(state => {
        state.inbox.messages = messages;
      }),
    appendMessage: (message: Message, type: ChatType = "member") => {
      if (
        message.community_inbox_chat_id !== Number(get().inbox.currentChatId)
      ) {
        set(state => {
          applyUnreadFlagForNewMessage(state.inbox, message, type);
        });
        return;
      }
      if (get().inbox.messages.some(m => m.id === message.id)) {
        return;
      }
      set(state => {
        state.inbox.messages = [...state.inbox.messages, message];
      });
    },
    updateMessage: (message: Message) => {
      if (
        message.community_inbox_chat_id !== Number(get().inbox.currentChatId)
      ) {
        return;
      }
      set(state => {
        const index = state.inbox.messages.findIndex(m => m.id === message.id);
        if (index !== -1) {
          state.inbox.messages[index] = message;
        }
      });
    },
    destroyMessage: (messageId: number) => {
      set(state => {
        state.inbox.messages = state.inbox.messages.filter(
          m => m.id !== messageId,
        );
      });
    },
    resetMessages: () =>
      set(state => {
        state.inbox.messages = [];
      }),
    scrollAction: ACTIONS.FIRST_LOAD,
    setScrollAction: (action: string) => {
      set(state => {
        state.inbox.scrollAction = action;
      });
    },
    currentBotProfile: null,
    setCurrentBotProfile: (profile: any) => {
      set(state => {
        state.inbox.currentBotProfile = profile;
      });
    },
    fetchTotalUnreadChatsCount: () =>
      communityInboxApi
        .unreadMemberChatsCount()
        .then(({ unread_chats_count }: { unread_chats_count: number }) => {
          set(state => {
            state.inbox.totalUnreadChatsCount = unread_chats_count;
          });
        })
        .catch((err: any) => {
          console.error(err);
        }),
    fetchPausedAiUnreadChatsCount: () =>
      communityInboxApi
        .unreadAdminPausedAiChatsCount()
        .then(({ unread_chats_count }: { unread_chats_count: number }) => {
          set(state => {
            state.inbox.totalUnreadPausedAiChatsCount = unread_chats_count;
            state.inbox.isTotalUnreadPausedAiChatsCountLoaded = true;
          });
        })
        .catch((err: any) => {
          console.error(err);
        }),
    setIsProcessing: (isProcessing: boolean, type: ChatType) => {
      set(state => {
        const chatsKeyName = type === "member" ? "memberChats" : "adminChats";
        const currentChat = state.inbox[chatsKeyName].find(
          chat => chat.id === state.inbox.currentChatId,
        );
        if (currentChat) {
          currentChat.is_processing = isProcessing;
        }
      });
    },
    setPreviewChatProcessing: (isProcessing: boolean) => {
      set(state => {
        const currentChat = state.inbox.previewChat;
        if (currentChat) {
          currentChat.is_processing = isProcessing;
        }
      });
    },
  },
});
